import {
  COMMUNICATION_MODE_LABELS,
  CommunicationMode,
  TicketAudience,
} from '@21k/shared';

import type { Asset } from '../../api/types';
import type { TicketSocketUpdate } from '@21k/shared';
import ConversationThread from '../../components/ConversationThread';
import { fileIcon, formatBytes } from '../../utils/ticketDisplay';
import { getAssetUrl } from '../../api/client';

type Props = {
  ticketId: string;
  audience: TicketAudience;
  description: string;
  descriptionAssets: Asset[];
  activeAssigneeId?: string | null;
  onRefresh: () => void;
  onTicketUpdate?: (update: TicketSocketUpdate) => void;
};

function AssetRow({ asset }: { asset: Asset }) {
  async function download() {
    const { url } = await getAssetUrl(asset.id);
    window.open(url, '_blank');
  }

  return (
    <div className="asset-inline">
      <span className="asset-icon">{fileIcon(asset.mimeType)}</span>
      <span className="asset-name">{asset.fileName}</span>
      <span className="asset-size">{formatBytes(asset.sizeBytes)}</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => void download()}>
        Download
      </button>
    </div>
  );
}

export default function OverviewTab({
  ticketId,
  audience,
  description,
  descriptionAssets,
  activeAssigneeId,
  onRefresh,
  onTicketUpdate,
}: Props) {
  const scrollPrefix = (
    <>
      <div className="ticket-detail-description-block">
        <h3 className="ticket-section-title">Description</h3>
        <p className="ticket-description">{description}</p>
        {descriptionAssets.length > 0 && (
          <div className="description-assets">
            {descriptionAssets.map((a) => (
              <AssetRow key={a.id} asset={a} />
            ))}
          </div>
        )}
      </div>
      <h3 className="ticket-section-title">Conversation</h3>
    </>
  );

  return (
    <div className="overview-tab">
      <ConversationThread
        ticketId={ticketId}
        activeAssigneeId={activeAssigneeId}
        onRefresh={onRefresh}
        onTicketUpdate={onTicketUpdate}
        layout="ticket-detail"
        scrollPrefix={scrollPrefix}
      />
      {audience === TicketAudience.INTERNAL && descriptionAssets.length === 0 && (
        <p className="text-muted conv-hint">Internal ticket — use the Files tab for attachments.</p>
      )}
    </div>
  );
}

export function communicationModeBadgeClass(mode: CommunicationMode): string {
  switch (mode) {
    case CommunicationMode.LIVE_CHAT:
    case CommunicationMode.WHATSAPP_CONVERSATION:
      return 'badge-success';
    case CommunicationMode.EMAIL_THREAD:
      return 'badge-info';
    case CommunicationMode.SMS_THREAD:
      return 'badge-warning';
    default:
      return 'badge-neutral';
  }
}

export function communicationModeLabel(mode: CommunicationMode): string {
  return COMMUNICATION_MODE_LABELS[mode] ?? mode;
}
